import { defineComponent as _defineComponent } from 'vue'

import { Context } from "@nuxt/types";
import {
  computed,
  defineComponent,
  onMounted,
  ssrRef,
  useContext,
  useMeta,
  useRoute,
  useStore,
} from "@nuxtjs/composition-api";

import { useAmplitude, useTranslate, useVuetify } from "@/composables";
import { getCollectionsStore } from "@/store/collections";
import { useCreatorShop } from "@/store/creator-shop";
import { getProfilesStore } from "@/store/profiles";
import { formatErrorMessage, handleErrorStatusCode, handleServiceError } from "@/utils/api";
import { NOT_FOUND } from "@/utils/enums/http-codes";
import logger, { logLevel } from "@/utils/logger";

const __default__ = defineComponent({
  name: "Collections",
  async asyncData({ params, error, store }: Context) {
    const displayName = params.displayName;
    const profilesStore = getProfilesStore(store);
    try {
      await profilesStore.fetchProfileByDisplayName(displayName);
      const profileId = profilesStore.getIdByDisplayName(displayName);
      if (!profileId) {
        error({ statusCode: NOT_FOUND, message: `Profile not found (${profileId})` });
        return params;
      }
    } catch (err) {
      logger(logLevel.ERROR, "Error loading collections list page", {}, err);
      error({
        statusCode: handleErrorStatusCode(err),
        message: `Error loading collections list page - ${formatErrorMessage(err)}`,
      });
    }
    return params;
  },
  head: {},
});

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {

const paginate = ssrRef(false, "paginate");

const store = useStore();
const context = useContext();
const amplitude = useAmplitude();
const route = useRoute();
const vuetify = useVuetify();
const { t } = useTranslate();

const collectionsStore = getCollectionsStore(store);
const profilesStore = getProfilesStore(store);

const displayName = computed(() => route.value?.params?.displayName);

const collections = computed(() => collectionsStore.creatorCollectionFeed(displayName.value)?.ids);
const profileId = computed(() => profilesStore.getIdByDisplayName(displayName.value));

const profileAvatar = computed(() => {
  return profilesStore.profiles[profileId.value ?? ""]?.avatarUrl;
});

const fetch = async () => {
  if (profileId.value && (paginate.value || !collectionsStore.hasFeedBeenFetched(displayName.value))) {
    try {
      await collectionsStore.fetchCreatorCollectionFeed({
        id: profileId.value,
        displayName: displayName.value,
        overwrite: false,
      });
    } catch (error) {
      return handleServiceError({
        context,
        error: {
          status: handleErrorStatusCode(error),
          errorCode: "creator-collections-not-found",
          developerMessage: `Error fetching creator collections (${displayName.value})`,
        },
        originalError: error,
        meta: { url: route.value?.fullPath },
      });
    }
  }
  paginate.value = true;
};

const collection = (collectionId: string) => {
  return collectionsStore.collections[collectionId];
};

const ltkIds = (collectionId: string) => {
  const displayImages = 3;
  const latestItems = collection(collectionId)?.latestItemIds?.slice(0, displayImages) || [];
  return latestItems.map((item) => collection(collectionId)?.latestItemMap[item]?.externalId)?.filter((item) => !!item);
};

const isLastItemOnRow = (index: number) => {
  const largeViewportCols = 4;
  const smallViewportCols = 2;
  switch (vuetify.breakpoint.name) {
    case "xs":
      if ((index + 1) % smallViewportCols === 0) {
        return true;
      }
      return false;
    default:
      if ((index + 1) % largeViewportCols === 0) {
        return true;
      }
      return false;
  }
};

onMounted(async () => {
  amplitude?.sendEvent("/page/publisher/view-all-creator-collections");
  useCreatorShop().setReferringShopTab();
  await fetch();
});

const title = t("My Collections"); // Title for the page
const pageTitle = t("Shop {displayName}'s Collections on LTK ", { displayName: displayName.value }); // Meta title for the page
const pageDescription = t("Shop the looks in these curated Collections from {displayName} on LTK ", {
  displayName: displayName.value,
}); // Description for the page

useMeta(() => {
  const head = context.$metaService.setPageMeta({
    title: pageTitle,
    customTitle: true,
    description: pageDescription,
  });

  if (profileAvatar.value) {
    const height = 400;
    const width = 400;
    const image = `${profileAvatar.value}?fit=crop&w=${width}&h=${height}`;

    head.meta?.push(...context.$metaService.setOGImg(image, width));
  }

  return head;
});

return { __sfc: true,paginate, store, context, amplitude, route, vuetify, t, collectionsStore, profilesStore, displayName, collections, profileId, profileAvatar, fetch, collection, ltkIds, isLastItemOnRow, title, pageTitle, pageDescription }
}

})